/**
 * Image plumbing the <img> tag and the upload button need.
 *
 * File and thumbnail URLs are plain URLs under `/api/v1`: they go straight into `src`
 * and the row lookup behind them enforces what an <img> request cannot (a session cookie
 * travels, but the choice of whose photos are visible is made by the route, not the tag).
 *
 * The down-scale before upload exists for the phone on a range-car park signal: the
 * server re-encodes everything anyway, but a 60 MP original does not need to cross the
 * radio to become the few hundred KB it was always going to be.
 */

const BASE = "/api/v1";

export const imageUrl = (id: string): string => `${BASE}/images/${id}/file`;
export const thumbUrl = (id: string): string => `${BASE}/images/${id}/thumb`;

/** Matches the server's cap: sending more than this edge is wasted signal. */
export const UPLOAD_MAX_EDGE = 3000;
const UPLOAD_QUALITY = 0.85;

/** A file this small goes up untouched: re-encoding it client-side only costs a
 *  generation of quality, because the server's encoder is the one that bounds size. */
const PASSTHROUGH_BYTES = 3 * 1024 * 1024;

/**
 * Down-scale to the server's long-edge cap and re-encode as JPEG.
 *
 * Drawing through a canvas is also what strips EXIF here at the source: a rotated phone
 * photo is rotated into its pixels by `imageOrientation: "from-image"` and the canvas
 * emits no metadata. A file that is already small enough is returned as it came.
 */
export async function downscale(file: File): Promise<Blob> {
  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    // Not decodable by this browser (or not an image at all): pass it through and let
    // the server's magic-byte check answer with the sentence the user should read.
    return file;
  }
  try {
    const edge = Math.max(bitmap.width, bitmap.height);
    if (edge <= UPLOAD_MAX_EDGE && file.size <= PASSTHROUGH_BYTES) return file;

    const scale = UPLOAD_MAX_EDGE / edge;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const context = canvas.getContext("2d");
    if (context === null) return file;
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const encoded = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", UPLOAD_QUALITY),
    );
    return encoded ?? file;
  } finally {
    bitmap.close();
  }
}

/** The FormData half of an upload, assembled here so every caller posts the same fields. */
export function targetImageForm(ids: {
  league_id: string;
  round_id: string;
  shooter_id: string;
  target_id: string;
}, blob: Blob, filename: string): FormData {
  const form = new FormData();
  form.set("league_id", ids.league_id);
  form.set("round_id", ids.round_id);
  form.set("shooter_id", ids.shooter_id);
  form.set("target_id", ids.target_id);
  form.set("file", blob, filename);
  return form;
}

/** Human byte count for captions and error copy. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
